import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useIsGreek } from "../../context/LanguageContext";
import { chartPointAtPixel, parseTickValues, validAnchors } from "./curveDigitizer";
import { traceCurveColor, pixelAtLoad, missingTraceLoads } from "./traceCurveColor";

const LEVELS = ["Vmin", "Vnom", "Vmax"];
const EMPTY_CURVES = [];
const COLORS = { Vmin: "#1f77b4", Vnom: "#2ca02c", Vmax: "#d62728" };
const STARTER_X_TICKS = "5 10 20 25 30 50 75 100";
const MIN_ZOOM = 25;
const MAX_ZOOM = 800;
const PAN_THRESHOLD = 4;
const EL = {
  "Choose a PNG, JPEG or WebP image of the chart.": "Επιλέξτε εικόνα PNG, JPEG ή WebP του γραφήματος.",
  "Select opposite corners of the chart area.": "Επιλέξτε δύο απέναντι γωνίες της περιοχής του γραφήματος.",
  "Enter at least two load tick values before marking the X axis.": "Καταχωρίστε τουλάχιστον δύο ενδείξεις φορτίου πριν σημειώσετε τον άξονα Χ.",
  "Mark X ticks in the order of the entered labels, from left to right.": "Σημειώστε τις ενδείξεις Χ με τη σειρά που καταχωρίστηκαν, από αριστερά προς τα δεξιά.",
  "Enter at least two efficiency tick values before marking the Y axis.": "Καταχωρίστε τουλάχιστον δύο ενδείξεις απόδοσης πριν σημειώσετε τον άξονα Υ.",
  "Mark Y ticks in the order of the entered labels, from top to bottom.": "Σημειώστε τις ενδείξεις Υ με τη σειρά που καταχωρίστηκαν, από πάνω προς τα κάτω.",
  "Click inside the calibrated axes. Add ticks around the full curve if needed.": "Κάντε κλικ μέσα στους βαθμονομημένους άξονες. Αν χρειάζεται, σημειώστε περισσότερες ενδείξεις ώστε να καλύπτεται όλη η καμπύλη.",
  "Digitize manufacturer efficiency curves": "Ψηφιοποίηση καμπυλών απόδοσης κατασκευαστή",
  "Paste a chart screenshot or upload an image. Mark the labelled ticks as printed; add every tick when the X-axis spacing is uneven. Only numeric points are sent to the fit.": "Επικολλήστε στιγμιότυπο του γραφήματος ή ανεβάστε εικόνα. Σημειώστε τις ενδείξεις όπως εμφανίζονται. Αν οι αποστάσεις στον Χ είναι άνισες, σημειώστε όλες τις ενδείξεις. Στο fit μεταφέρονται μόνο τα αριθμητικά σημεία.",
  "Upload chart image": "Ανέβασμα εικόνας γραφήματος",
  "A screenshot copied from the PDF can be pasted here with Ctrl+V.": "Μπορείτε να επικολλήσετε εδώ στιγμιότυπο από το PDF με Ctrl+V.",
  "X tick labels: load (%)": "Ενδείξεις Χ: φορτίο (%)",
  "Enter values from left to right, separated by spaces. Uneven spacing is supported.": "Γράψτε τις τιμές από αριστερά προς τα δεξιά, χωρισμένες με κενά. Υποστηρίζονται άνισες αποστάσεις.",
  "Y tick labels: efficiency (%)": "Ενδείξεις Υ: απόδοση (%)",
  "Enter values from top to bottom; two labelled lines are enough for a linear Y axis.": "Γράψτε τις τιμές από πάνω προς τα κάτω. Για γραμμικό άξονα Υ αρκούν δύο ενδείξεις.",
  "Crop": "Περικοπή",
  "Mark X ticks": "Σήμανση ενδείξεων Χ",
  "Mark Y ticks": "Σήμανση ενδείξεων Υ",
  "Pick curve points": "Επιλογή σημείων καμπύλης",
  "Zoom (%)": "Μεγέθυνση (%)",
  "Ctrl + mouse wheel: zoom at the pointer. Drag to move the image; click to mark a point.": "Ctrl + ροδέλα: μεγέθυνση στη θέση του δείκτη. Σύρετε για μετακίνηση της εικόνας· κάντε απλό κλικ για σημείο.",
  "Reset zoom": "Επαναφορά μεγέθυνσης",
  "A point at this load already exists. Delete it first if you want to replace it.": "Υπάρχει ήδη σημείο σε αυτό το φορτίο. Διαγράψτε το πρώτα αν θέλετε να το αντικαταστήσετε.",
  "Manufacturer efficiency chart": "Γράφημα απόδοσης κατασκευαστή",
  "DC voltage (V)": "Τάση DC (V)",
  "captured points": "σημεία",
  "Curve to capture": "Καμπύλη για ψηφιοποίηση",
  "Undo last point": "Αναίρεση τελευταίου σημείου",
  "Clear selected curve": "Καθαρισμός επιλεγμένης καμπύλης",
  "Enter the three measured DC voltages in increasing order; these are curve voltages, not the inverter MPPT limits.": "Καταχωρίστε τις τρεις τάσεις DC των καμπυλών σε αύξουσα σειρά. Δεν είναι τα όρια MPPT του αντιστροφέα.",
  "At least three points per voltage are required. Review and adjust the transferred numbers in the table before fitting.": "Απαιτούνται τουλάχιστον τρία σημεία ανά τάση. Ελέγξτε και διορθώστε τις τιμές στον πίνακα πριν από το fit.",
  "Cancel": "Ακύρωση",
  "Use digitized points": "Χρήση ψηφιοποιημένων σημείων",
  "Detect by color": "Ανίχνευση ανά χρώμα",
  "Color tolerance": "Ανοχή χρώματος",
  "Click the center of the curve's colored line. Avoid labels and intersections.": "Κάντε κλικ στο κέντρο της χρωματιστής γραμμής της καμπύλης. Αποφύγετε ετικέτες και διασταυρώσεις.",
  "Detection preview": "Προεπισκόπηση ανίχνευσης",
  "Review every point. Gaps and overlapping or same-color lines may stop or mislead detection. No missing sections are filled automatically.": "Ελέγξτε όλα τα σημεία. Κενά, επικαλύψεις ή γραμμές ίδιου χρώματος μπορεί να διακόψουν ή να παραπλανήσουν την ανίχνευση. Τα τμήματα που λείπουν δεν συμπληρώνονται αυτόματα.",
  "Click the image to add a preview point. Select a point below, then click its correct position on the image to move it. Use its × to delete it.": "Κάντε κλικ στην εικόνα για νέο σημείο προεπισκόπησης. Επιλέξτε ένα σημείο παρακάτω και μετά τη σωστή θέση στην εικόνα για να το μετακινήσετε. Με το × το διαγράφετε.",
  "Add preview points": "Προσθήκη σημείων προεπισκόπησης",
  "Accept preview": "Αποδοχή προεπισκόπησης",
  "Discard preview": "Απόρριψη προεπισκόπησης",
  "Existing captured points are kept; at exactly the same load, the existing point takes precedence.": "Τα υπάρχοντα σημεία διατηρούνται· σε ακριβώς ίδιο φορτίο διατηρείται το υπάρχον σημείο.",
  "No clear curve was found. Click on the line itself, adjust color tolerance or use manual capture.": "Δεν εντοπίστηκε σαφής καμπύλη. Κάντε κλικ πάνω στη γραμμή, προσαρμόστε την ανοχή χρώματος ή επιλέξτε σημεία χειροκίνητα.",
  "The image could not be read for detection. Use a smaller image or manual capture.": "Η εικόνα δεν ήταν δυνατό να διαβαστεί για ανίχνευση. Χρησιμοποιήστε μικρότερη εικόνα ή χειροκίνητη επιλογή.",
};

/** The image never leaves the browser. Only the reviewed numeric points enter
 * the existing Sandia fit flow. No chart scale or DC voltage is inferred. */
export default function InverterCurveDigitizer({ open, onClose, onUse, initialCurves = EMPTY_CURVES }) {
  const greek = useIsGreek();
  const t = (value) => greek ? EL[value] || value : value;
  const imageRef = useRef(null);
  const viewportRef = useRef(null);
  const releaseWheelRef = useRef(null);
  const zoomRef = useRef(100);
  const zoomAnchorRef = useRef(null);
  const panRef = useRef(null);
  const suppressClickRef = useRef(false);
  const objectUrlRef = useRef(null);
  const [imageSrc, setImageSrc] = useState("");
  const [mode, setMode] = useState("crop");
  const [cropStart, setCropStart] = useState(null);
  const [xTicks, setXTicks] = useState(STARTER_X_TICKS);
  const [yTicks, setYTicks] = useState("100 88");
  const [xAnchors, setXAnchors] = useState([]);
  const [yAnchors, setYAnchors] = useState([]);
  const [level, setLevel] = useState("Vmin");
  const [voltages, setVoltages] = useState({ Vmin: "", Vnom: "", Vmax: "" });
  const [points, setPoints] = useState({ Vmin: [], Vnom: [], Vmax: [] });
  const [zoom, setZoom] = useState(100);
  const [message, setMessage] = useState("");
  const [panning, setPanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [fillLoad, setFillLoad] = useState(null);
  const [colorTolerance, setColorTolerance] = useState(45);
  const clearPreview = () => { setPreview(null); setSelectedPreview(null); setFillLoad(null); };
  const missingLoads = preview ? missingTraceLoads(preview.targets, [...points[preview.level], ...preview.points]) : [];

  const startPan = (event) => {
    if (event.button !== 0 || event.isPrimary === false || panRef.current) return;
    const viewport = viewportRef.current;
    suppressClickRef.current = false;
    panRef.current = {
      pointerId: event.pointerId, x: event.clientX, y: event.clientY,
      left: viewport.scrollLeft, top: viewport.scrollTop, moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const movePan = (event) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    const dx = event.clientX - pan.x;
    const dy = event.clientY - pan.y;
    // Small hand movements still count as a click. Once dragging starts,
    // returning to the starting position must not turn it back into a click.
    if (!pan.moved && Math.hypot(dx, dy) < PAN_THRESHOLD) return;
    pan.moved = true;
    suppressClickRef.current = true;
    setPanning(true);
    viewportRef.current.scrollLeft = pan.left - dx;
    viewportRef.current.scrollTop = pan.top - dy;
  };

  const endPan = (event, cancelled = false) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    if (!cancelled) movePan(event);
    if (cancelled) suppressClickRef.current = true;
    panRef.current = null;
    setPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const changeZoom = useCallback((value, pointer) => {
    const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(value) || 100));
    if (next === zoomRef.current) return;
    const image = imageRef.current;
    const viewport = viewportRef.current;
    if (image && viewport) {
      const imageRect = image.getBoundingClientRect();
      const viewRect = viewport.getBoundingClientRect();
      const left = viewRect.left + viewport.clientLeft;
      const top = viewRect.top + viewport.clientTop;
      // At less than 100%, the viewport can contain blank space beside the
      // image. Numeric zoom should center the visible image, not that space.
      const clientX = pointer?.clientX ?? (Math.max(left, imageRect.left) + Math.min(left + viewport.clientWidth, imageRect.left + imageRect.width)) / 2;
      const clientY = pointer?.clientY ?? (Math.max(top, imageRect.top) + Math.min(top + viewport.clientHeight, imageRect.top + imageRect.height)) / 2;
      if (imageRect.width && imageRect.height) zoomAnchorRef.current = {
        x: (clientX - imageRect.left) / imageRect.width,
        y: (clientY - imageRect.top) / imageRect.height,
        offsetX: clientX - left,
        offsetY: clientY - top,
      };
    }
    zoomRef.current = next;
    setZoom(next);
  }, []);

  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const image = imageRef.current;
    const viewport = viewportRef.current;
    zoomAnchorRef.current = null;
    if (!anchor || !image || !viewport) return;
    const imageRect = image.getBoundingClientRect();
    const viewRect = viewport.getBoundingClientRect();
    // Restore the same image coordinate under the pointer after resizing.
    viewport.scrollLeft += imageRect.left + anchor.x * imageRect.width - viewRect.left - viewport.clientLeft - anchor.offsetX;
    viewport.scrollTop += imageRect.top + anchor.y * imageRect.height - viewRect.top - viewport.clientTop - anchor.offsetY;
  }, [zoom]);

  const bindViewport = useCallback((viewport) => {
    releaseWheelRef.current?.();
    releaseWheelRef.current = null;
    viewportRef.current = viewport;
    if (!viewport) return;
    const wheel = (event) => {
      if (!event.ctrlKey) return;
      // Bind on the actual DOM mount. The dialog portal can mount AFTER the
      // parent's effects on reopen, leaving an effect-based ref unbound.
      // Capture + non-passive also cancels browser zoom before bubbling.
      event.preventDefault();
      event.stopPropagation();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientHeight : 1;
      const delta = Math.max(-200, Math.min(200, event.deltaY * unit));
      changeZoom(zoomRef.current * Math.exp(-delta * 0.002), event);
    };
    viewport.addEventListener("wheel", wheel, { passive: false, capture: true });
    releaseWheelRef.current = () => viewport.removeEventListener("wheel", wheel, true);
  }, [changeZoom]);

  useEffect(() => {
    if (!open) return;
    setVoltages(Object.fromEntries(LEVELS.map((key) => [
      key, initialCurves.find((curve) => curve.level === key)?.dc_voltage || "",
    ])));
    setPoints({ Vmin: [], Vnom: [], Vmax: [] });
    setXAnchors([]);
    setYAnchors([]);
    setMode("crop");
    setCropStart(null);
    setMessage("");
    setPreview(null);
    setSelectedPreview(null);
    setFillLoad(null);
  }, [open, initialCurves]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const loadImageFile = (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setMessage(t("Choose a PNG, JPEG or WebP image of the chart."));
      return;
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setImageSrc(url);
    clearPreview();
    setMode("crop");
    setCropStart(null);
    setXAnchors([]);
    setYAnchors([]);
    setPoints({ Vmin: [], Vnom: [], Vmax: [] });
    zoomRef.current = 100;
    zoomAnchorRef.current = null;
    setZoom(100);
    setMessage("");
  };

  const xValues = parseTickValues(xTicks);
  const yValues = parseTickValues(yTicks);
  const xReady = validAnchors(xAnchors) && xAnchors.length === xValues?.length;
  const yReady = validAnchors(yAnchors) && yAnchors.length === yValues?.length;
  const voltageNumbers = LEVELS.map((key) => Number(voltages[key]));
  const voltagesReady = voltageNumbers.every((value) => Number.isFinite(value) && value > 0) &&
    voltageNumbers[0] < voltageNumbers[1] && voltageNumbers[1] < voltageNumbers[2];
  const ready = !preview && mode !== "detect" && xReady && yReady && voltagesReady && LEVELS.every((key) => points[key].length >= 3);

  const resetAxis = (axis) => {
    clearPreview();
    if (axis === "x") setXAnchors([]);
    else setYAnchors([]);
    setPoints({ Vmin: [], Vnom: [], Vmax: [] });
    setMode(axis);
    setMessage("");
  };

  const clickImage = (event) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    const image = imageRef.current;
    if (!image?.naturalWidth) return;
    const rect = image.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setMessage("");

    if (mode === "detect") {
      try {
        if (image.naturalWidth * image.naturalHeight > 16000000 ||
            image.naturalWidth > 8192 || image.naturalHeight > 8192) throw new Error("Image too large");
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(image, 0, 0);
        const detected = traceCurveColor(context.getImageData(0, 0, canvas.width, canvas.height),
          xAnchors, yAnchors, { x, y }, colorTolerance);
        if (!detected.coverage) {
          setMessage(t("No clear curve was found. Click on the line itself, adjust color tolerance or use manual capture."));
          return;
        }
        setPreview({ ...detected, level });
        setSelectedPreview(null);
        setFillLoad(null);
        setMode("review");
      } catch {
        setMessage(t("The image could not be read for detection. Use a smaller image or manual capture."));
      }
      return;
    }

    if (mode === "crop") {
      if (!cropStart) {
        setCropStart({ x, y });
        return;
      }
      const left = Math.min(cropStart.x, x);
      const top = Math.min(cropStart.y, y);
      const width = Math.abs(x - cropStart.x);
      const height = Math.abs(y - cropStart.y);
      if (width < 0.025 || height < 0.025) {
        setMessage(t("Select opposite corners of the chart area."));
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * image.naturalWidth));
      canvas.height = Math.max(1, Math.round(height * image.naturalHeight));
      canvas.getContext("2d").drawImage(
        image,
        left * image.naturalWidth, top * image.naturalHeight,
        width * image.naturalWidth, height * image.naturalHeight,
        0, 0, canvas.width, canvas.height,
      );
      setImageSrc(canvas.toDataURL("image/png"));
      setCropStart(null);
      resetAxis("x");
      return;
    }

    if (mode === "x") {
      if (!xValues || xAnchors.length >= xValues.length) {
        setMessage(t("Enter at least two load tick values before marking the X axis."));
        return;
      }
      const next = [...xAnchors, { pixel: x, value: xValues[xAnchors.length] }];
      if (next.length > 1 && !validAnchors(next)) {
        setMessage(t("Mark X ticks in the order of the entered labels, from left to right."));
        return;
      }
      setXAnchors(next);
      if (next.length === xValues.length) setMode("y");
      return;
    }

    if (mode === "y") {
      if (!yValues || yAnchors.length >= yValues.length) {
        setMessage(t("Enter at least two efficiency tick values before marking the Y axis."));
        return;
      }
      const next = [...yAnchors, { pixel: y, value: yValues[yAnchors.length] }];
      if (next.length > 1 && !validAnchors(next)) {
        setMessage(t("Mark Y ticks in the order of the entered labels, from top to bottom."));
        return;
      }
      setYAnchors(next);
      if (next.length === yValues.length) setMode("point");
      return;
    }

    const point = chartPointAtPixel(xAnchors, yAnchors,
      mode === "review" && fillLoad != null ? pixelAtLoad(xAnchors, fillLoad) : x, y);
    if (!point) {
      setMessage(t("Click inside the calibrated axes. Add ticks around the full curve if needed."));
      return;
    }
    if (mode === "review" && preview) {
      if (preview.points.some((item, index) => index !== selectedPreview && item.load === point.load)) {
        setMessage(t("A point at this load already exists. Delete it first if you want to replace it."));
        return;
      }
      setPreview(previous => ({ ...previous, points: selectedPreview == null
        ? [...previous.points, point]
        : previous.points.map((item, index) => index === selectedPreview ? point : item) }));
      setSelectedPreview(null);
      setFillLoad(null);
      return;
    }
    if (points[level].some((item) => item.load === point.load)) {
      setMessage(t("A point at this load already exists. Delete it first if you want to replace it."));
      return;
    }
    setPoints((previous) => ({ ...previous, [level]: [...previous[level], point] }));
  };

  const transfer = () => {
    if (!ready) return;
    onUse(LEVELS.map((key) => ({
      level: key,
      dc_voltage: voltageNumbers[LEVELS.indexOf(key)],
      points: [...points[key]].sort((a, b) => a.load - b.load)
        .map(({ load, efficiency }) => ({ load, efficiency })),
    })));
    onClose();
  };

  const acceptPreview = () => {
    if (!preview?.points.length || missingLoads.length) return;
    setPoints(previous => ({ ...previous, [preview.level]: [
      ...previous[preview.level],
      ...preview.points.filter(point => !previous[preview.level].some(item => item.load === point.load)),
    ] }));
    clearPreview();
    setMode("point");
    setMessage("");
  };

  const currentTick = mode === "x" ? xValues?.[xAnchors.length] :
    mode === "y" ? yValues?.[yAnchors.length] : null;
  const instructions = mode === "detect"
    ? t("Click the center of the curve's colored line. Avoid labels and intersections.")
    : mode === "review"
      ? fillLoad != null
        ? (greek ? `Συμπλήρωση στο ${fillLoad}% φορτίο: κάντε κλικ στο ύψος της σωστής καμπύλης. Το φορτίο παραμένει σταθερό.` : `Fill ${fillLoad}% load: click the correct curve height. The load stays fixed.`)
        : t("Click the image to add a preview point. Select a point below, then click its correct position on the image to move it. Use its × to delete it.")
    : mode === "crop"
    ? cropStart
      ? (greek ? "Κάντε κλικ στην απέναντι γωνία για περικοπή. Esc: ακύρωση της πρώτης γωνίας." : "Click the opposite corner to crop the chart. Esc: cancel the first corner.")
      : (greek ? "Κάντε κλικ στην πρώτη γωνία του γραφήματος. Η περικοπή είναι προαιρετική." : "Click the first corner of the chart. Cropping is optional.")
    : mode === "x"
      ? (greek ? `Κάντε κλικ στην ένδειξη Χ ${currentTick ?? "—"}%.` : `Click the X-axis tick labelled ${currentTick ?? "—"}%.`)
      : mode === "y"
        ? (greek ? `Κάντε κλικ στην ένδειξη Υ ${currentTick ?? "—"}%.` : `Click the Y-axis tick labelled ${currentTick ?? "—"}%.`)
        : (greek ? `Επιλέξτε καμπύλη τάσης και κάντε κλικ στα σημεία της. Τρέχουσα: ${level}.` : `Select a voltage curve, then click points along it. Current: ${level}.`);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      onKeyDownCapture={(event) => {
        if (event.key !== "Escape" || mode !== "crop" || !cropStart) return;
        event.preventDefault();
        event.stopPropagation();
        setCropStart(null);
        setMessage("");
      }}>
      <DialogTitle>{t("Digitize manufacturer efficiency curves")}</DialogTitle>
      <DialogContent dividers tabIndex={0} onPaste={(event) => {
        const file = [...(event.clipboardData?.files || [])].find((item) => item.type.startsWith("image/"));
        if (file) { event.preventDefault(); loadImageFile(file); }
      }}>
        <Stack spacing={1.5}>
          <Alert severity="info">
            {t("Paste a chart screenshot or upload an image. Mark the labelled ticks as printed; add every tick when the X-axis spacing is uneven. Only numeric points are sent to the fit.")}
          </Alert>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button component="label" variant="outlined">
              {t("Upload chart image")}
              <input hidden type="file" aria-label={t("Upload chart image")} accept="image/png,image/jpeg,image/webp" onChange={(event) => {
                loadImageFile(event.target.files?.[0]);
                event.target.value = "";
              }} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t("A screenshot copied from the PDF can be pasted here with Ctrl+V.")}
            </Typography>
          </Stack>
          {imageSrc && <>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label={t("X tick labels: load (%)")}
                  value={xTicks} onChange={(event) => { resetAxis("x"); setXTicks(event.target.value); }}
                  helperText={t("Enter values from left to right, separated by spaces. Uneven spacing is supported.")} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label={t("Y tick labels: efficiency (%)")}
                  value={yTicks} onChange={(event) => { resetAxis("y"); setYTicks(event.target.value); }}
                  helperText={t("Enter values from top to bottom; two labelled lines are enough for a linear Y axis.")} />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button size="small" variant={mode === "crop" ? "contained" : "outlined"}
                onClick={() => { clearPreview(); setMode("crop"); setCropStart(null); }}>
                {t("Crop")}
              </Button>
              <Button size="small" variant={mode === "x" ? "contained" : "outlined"}
                onClick={() => resetAxis("x")}>{t("Mark X ticks")}</Button>
              <Button size="small" variant={mode === "y" ? "contained" : "outlined"}
                onClick={() => resetAxis("y")}>{t("Mark Y ticks")}</Button>
              <Button size="small" variant={mode === "point" ? "contained" : "outlined"}
                disabled={!xReady || !yReady} onClick={() => { clearPreview(); setMode("point"); }}>{t("Pick curve points")}</Button>
              <Button size="small" variant={mode === "detect" || preview ? "contained" : "outlined"}
                disabled={!xReady || !yReady} onClick={() => { clearPreview(); setMode("detect"); setMessage(""); }}>{t("Detect by color")}</Button>
              <TextField size="small" type="number" label={t("Color tolerance")} value={colorTolerance}
                disabled={Boolean(preview)} onChange={event => setColorTolerance(Math.max(5, Math.min(100, Number(event.target.value) || 5)))}
                slotProps={{ htmlInput: { min: 5, max: 100, step: 5 } }} sx={{ width: 150 }} />
              <Chip size="small" label={`X ${xAnchors.length}/${xValues?.length || 0} · Y ${yAnchors.length}/${yValues?.length || 0}`} />
              <TextField size="small" type="number" label={t("Zoom (%)")}
                value={Number(zoom.toFixed(1))} onChange={(event) => changeZoom(event.target.value)}
                slotProps={{ htmlInput: { min: MIN_ZOOM, max: MAX_ZOOM, step: 25 } }}
                sx={{ width: 115 }} />
              <Button size="small" onClick={() => changeZoom(100)}>{t("Reset zoom")}</Button>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{instructions}</Typography>
            <Typography variant="caption" color="text.secondary">{t("Ctrl + mouse wheel: zoom at the pointer. Drag to move the image; click to mark a point.")}</Typography>
            <Box ref={bindViewport} data-testid="digitizer-viewport" sx={{ overflow: "auto", overflowAnchor: "none", overscrollBehavior: "contain", maxHeight: 520, border: "1px solid", borderColor: "divider",
              borderRadius: 1, bgcolor: "#f8fafc", p: 1 }}>
              <Box data-testid="digitizer-image-plane" sx={{ position: "relative", width: `${zoom}%`, minWidth: 0, lineHeight: 0 }}>
                <Box component="img" ref={imageRef} src={imageSrc} alt={t("Manufacturer efficiency chart")}
                  draggable={false} onClick={clickImage}
                  onPointerDown={startPan} onPointerMove={movePan} onPointerUp={endPan}
                  onPointerCancel={(event) => endPan(event, true)}
                  onLostPointerCapture={(event) => endPan(event, true)}
                  sx={{ display: "block", width: "100%", height: "auto", cursor: panning ? "grabbing" : "crosshair", touchAction: "none", userSelect: "none" }} />
                {preview && fillLoad != null && <Box sx={{ position: "absolute", top: 0, bottom: 0,
                  left: `${pixelAtLoad(xAnchors, fillLoad) * 100}%`, borderLeft: "2px dashed #ec4899", pointerEvents: "none" }} />}
                {[
                  ...xAnchors.map((anchor) => ({ x: anchor.pixel, y: 0.98, text: String(anchor.value), color: "#2563eb" })),
                  ...yAnchors.map((anchor) => ({ x: 0.02, y: anchor.pixel, text: String(anchor.value), color: "#7c3aed" })),
                  ...LEVELS.flatMap((key) => points[key].map((point) => ({ ...point, isPoint: true, color: COLORS[key] }))),
                  ...(preview?.points || []).map((point, index) => ({ ...point, isPoint: true,
                    color: index === selectedPreview ? "#ec4899" : "#e68a00" })),
                  ...(cropStart ? [{ ...cropStart, text: "+", color: "#d97706" }] : []),
                ].map((marker, index) => (
                  <Box key={index} sx={{ position: "absolute", left: `${marker.x * 100}%`, top: `${marker.y * 100}%`,
                    transform: "translate(-50%, -50%)", borderRadius: "50%", border: "2px solid white",
                    bgcolor: marker.color, color: "white", fontSize: 10, fontWeight: 700,
                    width: marker.isPoint ? 8 : undefined, height: marker.isPoint ? 8 : undefined,
                    px: marker.isPoint ? 0 : 0.5, lineHeight: "18px", pointerEvents: "none", whiteSpace: "nowrap", boxShadow: 1 }}>
                    {marker.text}
                  </Box>
                ))}
              </Box>
            </Box>
            {preview && <Box role="region" aria-label={t("Detection preview")} sx={{ border: "1px solid", borderColor: "warning.main", borderRadius: 1, p: 1.5 }}>
              <Stack spacing={1}>
                <Typography fontWeight={700}>{t("Detection preview")} · {preview.level} · {preview.points.length}</Typography>
                <Typography variant="body2">{greek ? "Ανιχνευμένο εύρος φορτίου" : "Detected load range"}: {preview.coverage.map(value => Number(value?.toFixed(2))).join("–")}%</Typography>
                <Typography variant="body2">{greek ? "Δειγματοληψία στα ίδια φορτία για όλες τις τάσεις" : "Same sampling loads for all voltages"}: {preview.targets.join(", ")}%.
                  {greek ? " Δεν μεταφέρονται εκατοντάδες σημεία λόγω πάχους ή ανάλυσης της εικόνας." : " Image thickness or resolution does not add hundreds of fit points."}</Typography>
                {missingLoads.length > 0 && <Alert severity="error">
                  {greek ? "Ελλιπής κάλυψη. Συμπληρώστε τα παρακάτω φορτία πριν την αποδοχή. Επιλέξτε φορτίο και κάντε κλικ στο ύψος της καμπύλης· δεν γίνεται αυτόματη προέκταση." : "Incomplete coverage. Fill the following loads before accepting. Select a load and click the curve height; no automatic extrapolation is applied."}
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    {missingLoads.map(load => <Button key={load} size="small" variant={fillLoad === load ? "contained" : "outlined"}
                      onClick={() => { setFillLoad(load); setSelectedPreview(null); }}>
                      {greek ? "Συμπλήρωση" : "Fill"} {load}%</Button>)}
                  </Stack>
                </Alert>}
                <Alert severity="warning">{t("Review every point. Gaps and overlapping or same-color lines may stop or mislead detection. No missing sections are filled automatically.")}</Alert>
                <Typography variant="body2">{t("Existing captured points are kept; at exactly the same load, the existing point takes precedence.")}</Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ maxHeight: 180, overflow: "auto" }}>
                  {preview.points.map((point, index) => <Chip key={index} size="small"
                    color={selectedPreview === index ? "primary" : "default"}
                    label={`${index + 1}: ${Number(point.load.toFixed(4))}% → ${Number(point.efficiency.toFixed(4))}%`}
                    onClick={() => { setSelectedPreview(index); setFillLoad(null); }}
                    onDelete={() => { setPreview(previous => ({ ...previous, points: previous.points.filter((_, i) => i !== index) })); setSelectedPreview(null); setFillLoad(null); }} />)}
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button onClick={() => { setSelectedPreview(null); setFillLoad(null); }}>{t("Add preview points")}</Button>
                  <Button variant="contained" disabled={!preview.points.length || missingLoads.length > 0} onClick={acceptPreview}>{t("Accept preview")}</Button>
                  <Button onClick={() => { clearPreview(); setMode("point"); }}>{t("Discard preview")}</Button>
                </Stack>
              </Stack>
            </Box>}
            <Grid container spacing={1} alignItems="center">
              {LEVELS.map((key) => (
                <Grid key={key} size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" type="number" label={`${key} · ${t("DC voltage (V)")}`}
                    value={voltages[key]} onChange={(event) => setVoltages((previous) => ({ ...previous, [key]: event.target.value }))}
                    helperText={`${points[key].length} ${t("captured points")}`} />
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField select size="small" label={t("Curve to capture")} value={level}
                disabled={Boolean(preview)}
                onChange={(event) => setLevel(event.target.value)} sx={{ minWidth: 210 }}>
                {LEVELS.map((key) => <MenuItem key={key} value={key}>
                  {key}{voltages[key] ? ` · ${voltages[key]} V` : ""}
                </MenuItem>)}
              </TextField>
              <Button size="small" disabled={Boolean(preview) || !points[level].length} onClick={() =>
                setPoints((previous) => ({ ...previous, [level]: previous[level].slice(0, -1) }))}>
                {t("Undo last point")}
              </Button>
              <Button size="small" disabled={Boolean(preview) || !points[level].length} onClick={() =>
                setPoints((previous) => ({ ...previous, [level]: [] }))}>
                {t("Clear selected curve")}
              </Button>
            </Stack>
            {points[level].length > 0 && <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {points[level].map((point, index) => <Chip key={`${point.load}-${index}`} size="small"
                label={`${point.load}% → ${point.efficiency}%`}
                onDelete={() => setPoints((previous) => ({
                  ...previous,
                  [level]: previous[level].filter((_, pointIndex) => pointIndex !== index),
                }))} />)}
            </Stack>}
            {message && <Alert severity="warning">{message}</Alert>}
            {!voltagesReady && <Typography variant="caption" color="text.secondary">
              {t("Enter the three measured DC voltages in increasing order; these are curve voltages, not the inverter MPPT limits.")}
            </Typography>}
            <Typography variant="caption" color="text.secondary">
              {t("At least three points per voltage are required. Review and adjust the transferred numbers in the table before fitting.")}
            </Typography>
          </>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="contained" disabled={!ready} onClick={transfer}>
          {t("Use digitized points")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
